<script setup>
import { computed } from "vue";
import Spinner from "@/components/Spinner/Spinner.vue";

const props = defineProps({
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["extra-small", "small", "medium", "large"].includes(value)
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  isActive: {
    type: Boolean,
    default: false
  },
  iconOnly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["toggle"]);

const buttonClasses = computed(() => [
  "button-fixed-secondary",
  `button-fixed-secondary--${props.size}`,
  {
    "button-fixed-secondary--icon-only": props.iconOnly,
    "button-fixed-secondary--active": props.isActive,
    "button-fixed-secondary--inactive": !props.isActive,
    "button-fixed-secondary--active--loading": props.loading && props.isActive,
    "button-fixed-secondary--inactive--loading": props.loading && !props.isActive
  }
]);
</script>

<template>
  <button
    :disabled="props.disabled"
    :class="buttonClasses"
    :aria-pressed="props.isActive ? 'true' : 'false'"
    type="button"
    @click="emit('toggle')"
  >
    <Spinner
      v-if="loading"
      class="spinner"
    />
    <slot
      v-else
      name="icon-left"
    />
    <slot />
    <slot name="icon-right" />
  </button>
</template>

<style scoped lang="scss">

.button-fixed-secondary {
  // Сброс дефолтных стилей.
  border: none;
  outline: none;
  background: none;
  margin: 0;
  font: inherit;

  // Стили кнопки.
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  padding: 0 14px;

  border-radius: 50px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  cursor: pointer;
  user-select: none;

  // Если кнопка содержит только иконку - убираем отступы, задаем ширину равной высоте.
  &--icon-only {
    padding: 0;
    min-width: var(--button-height);
    width: var(--button-height);
  }

  // Активное состояние
  &--active {
    background: var(--fix-button-secondary-active-default);
    color: var(--fix-button-secondary-active-text);

    &:hover:not(:disabled):not(&--loading) {
      background: var(--fix-button-secondary-active-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--fix-button-secondary-active-text);
      outline-offset: 2px;
    }

    &:disabled {
      pointer-events: none;
      background: var(--fix-button-secondary-active-disabled);
    }

    &--loading {
      background: var(--fix-button-secondary-active-loading);

      ::v-deep(.spinner) {
        border-color: var(--fix-button-secondary-active-text);
        border-bottom-color: transparent;
      }
    }
  }

  // Неактивное состояние
  &--inactive {
    background: var(--fix-button-secondary-inactive-default);
    color: var(--fix-button-secondary-inactive-text);

    &:hover:not(:disabled):not(&--loading) {
      background: var(--fix-button-secondary-inactive-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--fix-button-secondary-inactive-text);
      outline-offset: 2px;
    }

    &:disabled {
      pointer-events: none;
      background: var(--fix-button-secondary-inactive-disabled);
      color: var(--text-text-disabled);
    }

    &--loading {
      background: var(--fix-button-secondary-inactive-loading);

      ::v-deep(.spinner) {
        border-color: var(--fix-button-secondary-inactive-text);
        border-bottom-color: transparent;
      }
    }
  }

  // Размеры
  &--extra-small {
    --button-height: 24px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--small {
    --button-height: 30px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--medium {
    --button-height: 36px;
    height: var(--button-height);
    min-height: var(--button-height);
  }

  &--large {
    --button-height: 42px;
    height: var(--button-height);
    min-height: var(--button-height);
  }
}

</style>